/**
 *
 *  Booking Panel
 *  Component
 *
 *  Created by Vladimir Shislyannikov on 09/12/2021.
 *  Copyright (c) 2021 iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useCallback, useState, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Animated, StyleSheet, View } from "react-native";

// Components
import SlidingUpPanel from "rn-sliding-up-panel";

// Hooks
import useHeight from "../../hooks/useHeight/useHeight";
import useBodyScrollLock from "../../hooks/useBodyScrollLock/useBodyScrollLock";

const defaultProps = {
  isOpening: true,
  allowDragging: false,
};

const CLOSE_TIMEOUT = 300;

/**
 * @public
 */
export const Drawer = React.forwardRef(
  (
    {
      children,
      isOpen,
      allowDragging,
      title: TitleElement,
      titleElementProps,
      containerStyle,
      draggableRange,
      snappingPoints,
      height,
      attachRef,
      attachContainerRef,
      onFullyOpen,
      onBottomReached,
      onClose,
    },
    outerRef
  ) => {
    const [_isOpen, setIsOpen] = useState(isOpen);
    const [isFullyOpen, setIsFullyOpen] = useState(false);
    const [atBottom, setAtBottom] = useState(true);

    const deviceHeight = useHeight();
    const statusBarHeight = 24;

    const _draggableRange = draggableRange
      ? draggableRange
      : {
          top: height || deviceHeight - statusBarHeight,
          bottom: 0,
        };

    const [_snappingPoints, setSnappingPoints] = useState(
      snappingPoints
        ? snappingPoints
        : [
            height ? deviceHeight - statusBarHeight : _draggableRange.top,
            // draggableRange.bottom
          ]
    );

    const panelRef = React.useRef(null);
    const panelPositionValRef = React.useRef(
      new Animated.Value(_draggableRange.bottom)
    );
    const panelPositionValueListenerRef = React.useRef(null);
    const openRef = React.useRef(isOpen);

    const _attachRef = useCallback(
      (node) => {
        const ref = attachRef;

        panelRef.current = node;

        if (ref == null) {
          return;
        }

        if (typeof ref === "function") {
          ref(node);

          return;
        }

        if (typeof ref === "object") {
          ref.current = node;
        }
      },
      [attachRef]
    );

    useEffect(() => {
      openRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
      if (snappingPoints) {
        setSnappingPoints(snappingPoints);
      } else {
        // will the snappingPoints be set twice??
        setSnappingPoints([
          height ? deviceHeight - statusBarHeight : _draggableRange.top,
          // draggableRange.bottom
        ]);
      }
    }, [height, snappingPoints, _draggableRange.top]);

    const { /*bottomBarHeight, */ refCallback, targetRef } =
      useBodyScrollLock(_isOpen);

    const _attachContainerRef = useCallback(
      (node) => {
        const ref = attachContainerRef;

        // panelContainerRef.current = node;

        if (refCallback == null) {
          return;
        }

        if (typeof refCallback === "function") {
          refCallback(node);

          // return;
        } else if (typeof refCallback === "object") {
          refCallback.current = node;
        }

        if (ref != null) {
          if (typeof ref === "function") {
            ref(node);

            return;
          }

          if (typeof ref === "object") {
            ref.current = node;
          }
        }
      },
      [attachContainerRef, refCallback]
    );

    const onAnimatedValueChange = useCallback(
      ({ value }) => {
        if (value === _draggableRange.top) {
          // At top position
          setIsFullyOpen(true);
          setAtBottom(false);
          onFullyOpen && onFullyOpen();
        }

        if (value === _draggableRange.bottom) {
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [_draggableRange.top, _draggableRange.bottom]
    );

    const close = useCallback(() => {
      return new Promise((resolve) => {
        setIsFullyOpen(false);

        panelRef && panelRef.current && panelRef.current.hide();

        setTimeout(() => {
          panelPositionValRef.current.removeListener(
            panelPositionValueListenerRef.current
          );
          panelPositionValueListenerRef.current = null;
          resolve(null);
        }, CLOSE_TIMEOUT);
      });
    }, []);

    useEffect(() => {
      if (!attachRef.current) {
        if (!isOpen && _isOpen) {
          setIsOpen(false);
        }

        return;
      }

      if (isOpen && !_isOpen) {
        let targetEl = panelRef && panelRef.current;

        setAtBottom(false);

        targetEl && targetEl.show(_snappingPoints[0]);
      }

      if (isOpen && !_isOpen) {
        const targetEl = ReactDOM.findDOMNode(targetRef && targetRef.current);

        setIsOpen(true);

        targetEl.scrollTop = 0;
      } else if (!isOpen && _isOpen) {
        // We need setTimeout here, because useBodyScrollLock should have a ref.
        // Without it, it can't enable scroll back again
        setTimeout(() => {
          setIsOpen(false);
        }, CLOSE_TIMEOUT);

        close().then(() => {
          onClose && onClose(undefined, true); // Might cause issues when logic in callback also doing closing logic
        });
      }

      return () => {};
    }, [
      isOpen,
      attachRef,
      _snappingPoints,
      height,
      _isOpen,
      close,
      targetRef,
      onClose,
    ]);

    useEffect(() => {
      if (panelPositionValueListenerRef.current !== null) {
        panelPositionValRef.current.removeListener(
          panelPositionValueListenerRef.current
        );
        panelPositionValueListenerRef.current = null;
      }

      // When we close th panel, isOpen is still true for some time, so we need to rely on the prop
      if (openRef.current) {
        panelPositionValueListenerRef.current =
          panelPositionValRef.current.addListener(onAnimatedValueChange);

        panelRef &&
          panelRef.current &&
          panelRef.current.show(_snappingPoints[0]);
      }
    }, [
      _isOpen,
      _snappingPoints, // Updating it causes to open the panel again
      onAnimatedValueChange,
    ]);

    const handleBottomReached = useCallback(() => {
      setAtBottom(true);
      onBottomReached && onBottomReached();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onBottomReached]);

    return (
      <aside className="">
        {/*isOpen && backdropShow && <View style={panelStyles.backdrop} />*/}
        <SlidingUpPanel
          ref={_attachRef}
          animatedValue={panelPositionValRef.current}
          allowDragging={allowDragging}
          draggableRange={_draggableRange}
          snappingPoints={_snappingPoints}
          onBottomReached={handleBottomReached}
          backdropStyle={panelStyles.backdrop}
          containerStyle={panelStyles.container}
          backdropOpacity={atBottom ? 0 : 0.75}
          showBackdrop={true}
          height={height || deviceHeight - 24}
          friction={0.5}
          children={(dragHandlers) => {
            {
              // eslint-disable-line
              /* here should be props.isOpen because otherwise
            useBodyScrollLock can't enable scroll back again without the ref */
            }
            return (
              <View
                style={styles.container}
                {...(TitleElement ? {} : dragHandlers)}
              >
                {(_isOpen || true) && (
                  <>
                    {TitleElement ? (
                      <View style={styles.panelHeader} {...dragHandlers}>
                        <TitleElement {...titleElementProps} />
                      </View>
                    ) : null}
                    <div ref={_attachContainerRef} style={containerStyle}>
                      {children &&
                        React.cloneElement(children, {
                          isOpen: _isOpen,
                          isFullyOpen,
                          /*scrollEnabled, onMomentumScrollEnd, */ refCallback,
                          /*onScroll, */ close,
                        })}
                      {/*children*/}
                    </div>
                  </>
                )}
              </View>
            );
          }}
        ></SlidingUpPanel>
      </aside>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "white", // optimisations
    alignItems: "center",
    justifyContent: "stretch",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    height: "100%",
    overflowY: "scroll",
  },

  panelHeader: {
    width: "100%",
    paddingTop: 16,
    paddingLeft: "16px",
    paddingRight: "16px",
  },
});

const panelStyles = {
  backdrop: {
    position: "fixed",
    // position: 'absolute',
    backgroundColor: "black", // optimisations
    zIndex: 995,
  },
  container: {
    position: "fixed",
    // position: 'absolute',
    zIndex: 1004,
  },
};

Drawer.propTypes = {
  attachRef: PropTypes.object,
  attachContainerRef: PropTypes.object,
  isOpen: PropTypes.bool,
  isOpening: PropTypes.bool,
  allowDragging: PropTypes.bool,
  TitleElement: PropTypes.func,
  draggableRange: PropTypes.array,
  snappingPoints: PropTypes.array,
  containerStyle: PropTypes.object,
  onClose: PropTypes.func,
  onFullyOpen: PropTypes.func,
  onBottomReached: PropTypes.func,
};

Drawer.displayName = "TruBeDrawer";
Drawer.defaultProps = defaultProps;

export default Drawer;
